/**
 * A password that satisfies the platform policy by construction.
 *
 * `crypto.getRandomValues`, not `Math.random`: this is a real credential for a
 * real account from the moment it is submitted. Ambiguous characters (`I`, `l`,
 * `O`, `0`) are absent because the value gets read off a screen and typed by a
 * person.
 */

const GENERATED_LENGTH = 20;
const REQUIRED_CLASSES = 4;
const UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const LOWER = 'abcdefghijkmnopqrstuvwxyz';
const DIGITS = '23456789';
const SPECIALS = '!@#$%^&*-_=+';

function pick(source: string): string {
  const bytes = new Uint32Array(1);
  crypto.getRandomValues(bytes);
  return source[(bytes[0] ?? 0) % source.length] ?? source[0] ?? '';
}

export function generatePassword(): string {
  const alphabet = UPPER + LOWER + DIGITS + SPECIALS;

  const all = [
    pick(UPPER),
    pick(LOWER),
    pick(DIGITS),
    pick(SPECIALS),
    ...Array.from({ length: GENERATED_LENGTH - REQUIRED_CLASSES }, () => pick(alphabet)),
  ];

  for (let index = all.length - 1; index > 0; index -= 1) {
    const bytes = new Uint32Array(1);
    crypto.getRandomValues(bytes);
    const swap = (bytes[0] ?? 0) % (index + 1);
    const here = all[index];
    const there = all[swap];
    if (here === undefined || there === undefined) {
      continue;
    }
    all[index] = there;
    all[swap] = here;
  }

  return all.join('');
}
